import { ArrowLeft, ArrowUpRight, Check, FileCheck2, FileText, Landmark, ShieldAlert, ShieldCheck } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Footer } from "@/components/Footer";
import { Navbar } from "@/components/Navbar";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { ContactForm } from "@/components/ContactForm";
import { SEO_PAGE_CONFIG } from "@/config/seoPages";
import { SEO_PAGE_RU_CONTENT } from "@/config/seoPagesRu";
import { useLang } from "@/i18n/LangContext";

const SblcEducation = ({ content }) => (
  <section className="mt-20 border-y border-white/10 py-16 sm:py-20">
    <div className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-20">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{content.label}</p>
        <h2 className="mt-4 font-serif text-3xl text-[#F5F0E4] sm:text-4xl">{content.title}</h2>
      </div>
      <div>
        <p className="text-lg leading-relaxed text-slate-300">{content.intro}</p>
        <p className="mt-5 leading-relaxed text-slate-400">{content.definition}</p>
      </div>
    </div>

    <div className="mt-12 grid gap-5 md:grid-cols-3">
      {content.roles.map((item) => (
        <div key={item.title} className="rounded-2xl border border-white/10 bg-white/[0.035] p-6">
          <Landmark className="h-5 w-5 text-[#D4AF37]" />
          <h3 className="mt-5 font-serif text-2xl text-[#F5F0E4]">{item.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">{item.text}</p>
        </div>
      ))}
    </div>

    <div className="mt-14 grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{content.lifecycleLabel}</p>
        <h2 className="mt-4 font-serif text-3xl text-[#F5F0E4]">{content.lifecycleTitle}</h2>
        <p className="mt-4 max-w-md leading-relaxed text-slate-400">{content.lifecycleIntro}</p>
      </div>
      <ol className="space-y-5">
        {content.lifecycle.map((item, index) => (
          <li key={item} className="flex gap-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/45 text-xs font-semibold text-[#F5E5AB]">{String(index + 1).padStart(2, "0")}</span>
            <p className="pt-1 text-base leading-relaxed text-slate-300">{item}</p>
          </li>
        ))}
      </ol>
    </div>

    <div className="mt-14 grid gap-5 lg:grid-cols-2">
      <section className="rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/12 to-transparent p-7 sm:p-9">
        <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-[#F5E5AB]">{content.assuranceLabel}</p>
        <h2 className="mt-3 font-serif text-3xl text-[#F5F0E4]">{content.assuranceTitle}</h2>
        <p className="mt-4 leading-relaxed text-slate-200">{content.assuranceText}</p>
      </section>
      <section className="rounded-3xl border border-white/10 bg-white/[0.035] p-7 sm:p-9">
        <ShieldAlert className="h-6 w-6 text-[#D4AF37]" />
        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{content.limitLabel}</p>
        <h2 className="mt-3 font-serif text-3xl text-[#F5F0E4]">{content.limitTitle}</h2>
        <p className="mt-4 leading-relaxed text-slate-300">{content.limitText}</p>
      </section>
    </div>

    <section className="mt-14 rounded-3xl border border-white/10 bg-[#12141C]/80 p-7 sm:p-10">
      <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{content.termsLabel}</p>
          <h2 className="mt-4 font-serif text-3xl text-[#F5F0E4]">{content.termsTitle}</h2>
        </div>
        <div>
          <p className="text-lg leading-relaxed text-slate-200">{content.termsText}</p>
          <p className="mt-5 leading-relaxed text-slate-400">{content.termsAction}</p>
        </div>
      </div>
    </section>

    <section className="mt-14 grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-20">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{content.verifyLabel}</p>
        <h2 className="mt-4 font-serif text-3xl text-[#F5F0E4]">{content.verifyTitle}</h2>
      </div>
      <ul className="space-y-4">
        {content.checklist.map((item) => (
          <li key={item} className="flex gap-4 text-slate-300">
            <FileCheck2 className="mt-1 h-4 w-4 shrink-0 text-[#D4AF37]" />
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </section>

    {content.references?.length > 0 && (
      <section className="mt-14 border-t border-white/10 pt-8">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{content.referencesLabel}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          {content.references.map((reference) => (
            <a
              key={reference.href}
              href={reference.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-[#D4AF37]/60 hover:text-[#F5E5AB]"
            >
              {reference.label} <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          ))}
        </div>
        <p className="mt-5 max-w-4xl text-xs leading-relaxed text-slate-500">{content.disclaimer}</p>
      </section>
    )}
  </section>
);

export default function SeoServicePage() {
  const { pathname } = useLocation();
  const { lang } = useLang();
  const isRu = lang === "ru";
  const page = (isRu ? SEO_PAGE_RU_CONTENT : SEO_PAGE_CONFIG)[pathname];
  const hasEmbeddedContact = Boolean(page?.embeddedContact);
  const contactHref = hasEmbeddedContact ? "#sblc-contact" : "/#contact";
  const ui = isRu ? {
    home: "На главную MELNIK Commercial Brokers",
    discuss: "Обсудить знакомство",
    process: "Посмотреть процесс",
    purpose: "Назначение",
    start: "Понятная коммерческая отправная точка",
    how: "Как это работает",
    structured: "Структурированный путь к знакомству",
    howText: "Процесс помогает сделать начальное обсуждение предметным и заранее определить ответственность сторон до начала переговоров по конкретной сделке.",
    checklist: "Практический чек-лист",
    faq: "Частые вопросы",
    enquiry: "Коммерческий запрос",
    closing: "Обсудите, подходит ли вашей ситуации брокерское коммерческое знакомство.",
    closingText: "MELNIK рассмотрит общий коммерческий контекст и определит подходящий следующий шаг. Первичный запрос не подразумевает результата сделки."
  } : {
    home: "Back to MELNIK Commercial Brokers",
    discuss: "Discuss an introduction",
    process: "View the process",
    purpose: "Purpose",
    start: "A clear commercial starting point",
    how: "How it works",
    structured: "A structured route to an introduction",
    howText: "The process is designed to keep the early discussion purposeful and to make responsibilities clear before a transaction-specific negotiation begins.",
    checklist: "Practical checklist",
    faq: "Frequently asked questions",
    enquiry: "Commercial enquiry",
    closing: "Discuss whether a broker-led commercial introduction is suitable.",
    closingText: "MELNIK will review the high-level commercial context and identify the appropriate next step. No transaction outcome is implied by an initial enquiry."
  };

  if (!page) return null;

  return (
    <div id="top" className="min-h-screen bg-[#0A0B0E] text-slate-100">
      <div className="grain-overlay" />
      <Navbar />
      <main>
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(212,175,55,0.14),transparent_31%),radial-gradient(circle_at_10%_85%,rgba(10,64,108,0.18),transparent_35%)]" />
          <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-36 sm:px-8 sm:pb-24 sm:pt-44">
            <Link to="/" className="mb-10 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-[#F5E5AB]"><ArrowLeft className="h-4 w-4" /> {ui.home}</Link>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#D4AF37]">{page.eyebrow}</p>
            <h1 className="mt-5 max-w-4xl font-serif text-4xl leading-[1.04] text-[#F5F0E4] sm:text-6xl">{page.title}</h1>
            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-slate-300 sm:text-xl">{page.lead}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              {hasEmbeddedContact ? (
                <a data-testid="service-discuss-contact-button" href={contactHref} className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-semibold text-[#0A0B0E] transition-transform hover:scale-[1.02] active:scale-95">{ui.discuss} <ArrowUpRight className="h-4 w-4" /></a>
              ) : (
                <Link data-testid="service-discuss-contact-button" to={contactHref} className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-semibold text-[#0A0B0E] transition-transform hover:scale-[1.02] active:scale-95">{ui.discuss} <ArrowUpRight className="h-4 w-4" /></Link>
              )}
              <Link to="/gold-petroleum-transaction-process" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-[#D4AF37]/60 hover:text-[#F5E5AB]">{ui.process} <FileText className="h-4 w-4" /></Link>
            </div>
          </div>
        </section>

        <article className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <section className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-20">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{ui.purpose}</p>
              <h2 className="mt-4 font-serif text-3xl text-[#F5F0E4]">{ui.start}</h2>
            </div>
            <p className="text-lg leading-relaxed text-slate-300">{page.purpose}</p>
          </section>

          <section className="mt-20">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{page.focusLabel}</p>
            <div className={`mt-6 grid gap-5 ${page.focus.length === 4 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
              {page.focus.map((item) => (
                <div key={item.title} className="rounded-2xl border border-white/10 bg-white/[0.035] p-6">
                  <ShieldCheck className="h-5 w-5 text-[#D4AF37]" />
                  <h2 className="mt-5 font-serif text-2xl text-[#F5F0E4]">{item.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{item.text}</p>
                </div>
              ))}
            </div>
          </section>

          {page.education && <SblcEducation content={page.education} />}

          <section className="mt-20 grid gap-10 border-y border-white/10 py-16 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{ui.how}</p>
              <h2 className="mt-4 font-serif text-3xl text-[#F5F0E4]">{ui.structured}</h2>
              <p className="mt-4 max-w-md leading-relaxed text-slate-400">{ui.howText}</p>
            </div>
            <ol className="space-y-5">
              {page.workflow.map((item, index) => (
                <li key={item} className="flex gap-5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/45 text-xs font-semibold text-[#F5E5AB]">{String(index + 1).padStart(2, "0")}</span>
                  <p className="pt-1 text-base leading-relaxed text-slate-300">{item}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className="mt-20 grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-20">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{ui.checklist}</p>
              <h2 className="mt-4 font-serif text-3xl text-[#F5F0E4]">{page.notesTitle}</h2>
            </div>
            <ul className="space-y-4">
              {page.notes.map((item) => (
                <li key={item} className="flex gap-4 text-slate-300"><Check className="mt-1 h-4 w-4 shrink-0 text-[#D4AF37]" /><span className="leading-relaxed">{item}</span></li>
              ))}
            </ul>
          </section>

          <section className="mt-20 rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-7 sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{ui.faq}</p>
            <div className="mt-7 divide-y divide-white/10">
              {page.faqs.map((item) => (
                <div key={item.question} className="py-6 first:pt-0 last:pb-0">
                  <h2 className="font-serif text-2xl text-[#F5F0E4]">{item.question}</h2>
                  <p className="mt-3 max-w-4xl leading-relaxed text-slate-300">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          {hasEmbeddedContact ? (
            <div id="sblc-contact" className="mt-20 scroll-mt-24 rounded-3xl border border-[#D4AF37]/20 bg-gradient-to-b from-white/[0.025] to-transparent">
              <ContactForm />
            </div>
          ) : (
            <section className="mt-20 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{ui.enquiry}</p>
              <h2 className="mx-auto mt-4 max-w-3xl font-serif text-3xl text-[#F5F0E4] sm:text-4xl">{ui.closing}</h2>
              <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-slate-400">{ui.closingText}</p>
              <Link data-testid="service-closing-contact-button" to="/#contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-semibold text-[#0A0B0E] transition-transform hover:scale-[1.02] active:scale-95">{isRu ? "Связаться с MELNIK" : "Contact MELNIK"} <ArrowUpRight className="h-4 w-4" /></Link>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
